import type { DataFileContentProfile } from './telemetryCachePolicy';
import { OVERVIEW_CONTENT_VERSION } from './telemetryCachePolicy';

const DB_NAME = 'paddiq-session-content-cache';
const DB_VERSION = 1;
const STORE_NAME = 'entries';
const MANIFEST_KEY = 'paddiq-session-content-manifest-v1';
const MAX_DISK_BYTES = 250 * 1024 * 1024;

type CacheManifestEntry = {
  key: string;
  fileId: string;
  profile: DataFileContentProfile;
  savedAt: number;
  bytes: number;
};

const memoryCache = new Map<string, unknown>();
const memorySavedAt = new Map<string, number>();

function cacheKey(fileId: string, profile: DataFileContentProfile) {
  return profile === 'overview'
    ? `${fileId}:${profile}:${OVERVIEW_CONTENT_VERSION}`
    : `${fileId}:${profile}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
  });
}

async function idbGet(key: string): Promise<unknown | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(key);
    request.onsuccess = () => resolve((request.result as unknown | undefined) ?? null);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB read failed'));
  });
}

async function idbSet(key: string, value: unknown) {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(value, key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('IndexedDB write failed'));
  });
}

async function idbDelete(key: string) {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('IndexedDB delete failed'));
  });
}

async function idbClear() {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const request = tx.objectStore(STORE_NAME).clear();
      request.onerror = () => reject(request.error ?? new Error('IndexedDB clear failed'));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB clear failed'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB clear aborted'));
    });
  } finally {
    db.close();
  }
}

async function readManifest(): Promise<CacheManifestEntry[]> {
  const raw = localStorage.getItem(MANIFEST_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as CacheManifestEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function writeManifest(entries: CacheManifestEntry[]) {
  localStorage.setItem(MANIFEST_KEY, JSON.stringify(entries));
}

type EvictedCacheEntry = { entry: CacheManifestEntry; payload: unknown | null };

async function restoreEvictedEntries(evicted: EvictedCacheEntry[]) {
  for (const { entry, payload } of evicted) {
    if (payload != null) await idbSet(entry.key, payload);
  }
}

async function evictIfNeeded(entries: CacheManifestEntry[]) {
  let totalBytes = entries.reduce((sum, entry) => sum + entry.bytes, 0);
  const evicted: EvictedCacheEntry[] = [];
  if (totalBytes <= MAX_DISK_BYTES) {
    return { entries, evicted };
  }

  const sorted = [...entries].sort((left, right) => left.savedAt - right.savedAt);
  try {
    while (sorted.length > 0 && totalBytes > MAX_DISK_BYTES) {
      const removed = sorted.shift();
      if (!removed) break;
      const payload = await idbGet(removed.key);
      await idbDelete(removed.key);
      evicted.push({ entry: removed, payload });
      totalBytes -= removed.bytes;
    }
  } catch (error) {
    await restoreEvictedEntries(evicted);
    throw error;
  }

  return { entries: sorted, evicted };
}

export function getCachedSessionContentSync(fileId: string, profile: DataFileContentProfile) {
  const key = cacheKey(fileId, profile);
  if (memoryCache.has(key)) {
    return memoryCache.get(key);
  }

  if (profile === 'overview') {
    const fullKey = cacheKey(fileId, 'full');
    if (memoryCache.has(fullKey)) {
      return memoryCache.get(fullKey);
    }
  }

  return undefined;
}

export function getCachedSessionContentUpdatedAt(fileId: string, profile: DataFileContentProfile) {
  const key = cacheKey(fileId, profile);
  if (memorySavedAt.has(key)) {
    return memorySavedAt.get(key);
  }

  if (profile === 'overview') {
    return memorySavedAt.get(cacheKey(fileId, 'full'));
  }

  return undefined;
}

export async function readCachedSessionContent(fileId: string, profile: DataFileContentProfile) {
  const sync = getCachedSessionContentSync(fileId, profile);
  if (sync !== undefined) {
    return sync;
  }

  const preferredKeys =
    profile === 'overview'
      ? [cacheKey(fileId, 'overview'), cacheKey(fileId, 'full')]
      : [cacheKey(fileId, profile)];

  const manifest = await readManifest();

  for (const key of preferredKeys) {
    const payload = await idbGet(key);
    if (payload == null) {
      continue;
    }

    const entry = manifest.find((item) => item.key === key);
    const savedAt = entry?.savedAt ?? Date.now();
    memoryCache.set(key, payload);
    memorySavedAt.set(key, savedAt);
    if (profile === 'overview' && key.endsWith(':full')) {
      memoryCache.set(cacheKey(fileId, 'overview'), payload);
      memorySavedAt.set(cacheKey(fileId, 'overview'), savedAt);
    }

    return payload;
  }

  return null;
}

export async function writeCachedSessionContent(
  fileId: string,
  profile: DataFileContentProfile,
  payload: unknown,
) {
  const key = cacheKey(fileId, profile);
  const savedAt = Date.now();
  const serialized = JSON.stringify(payload);

  const previousPayload = await idbGet(key);
  let evicted: EvictedCacheEntry[] = [];
  try {
    await idbSet(key, payload);
    const manifest = await readManifest();
    const withoutKey = manifest.filter((entry) => entry.key !== key);
    withoutKey.push({ key, fileId, profile, savedAt, bytes: serialized.length });

    const result = await evictIfNeeded(withoutKey);
    evicted = result.evicted;
    await writeManifest(result.entries);
    for (const { entry } of evicted) {
      memoryCache.delete(entry.key);
      memorySavedAt.delete(entry.key);
    }
    memoryCache.set(key, payload);
    memorySavedAt.set(key, savedAt);
  } catch (error) {
    await restoreEvictedEntries(evicted);
    if (previousPayload == null) await idbDelete(key);
    else await idbSet(key, previousPayload);
    throw error;
  }
}

export async function clearSessionContentCache() {
  memoryCache.clear();
  memorySavedAt.clear();
  localStorage.removeItem(MANIFEST_KEY);
  await idbClear();
}

export async function warmSessionContentCache(
  fileIds: string[],
  profile: DataFileContentProfile = 'overview',
) {
  await Promise.all(
    fileIds.map((fileId) => readCachedSessionContent(fileId, profile).catch(() => null)),
  );
}
